import React from 'react';
import { Shield, Eye, Lock, UserCheck, FileText, Mail } from 'lucide-react';

export const PrivacyPolicy = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-orange-100 p-4 rounded-full">
              <Shield size={48} className="text-orange-600" />
            </div>
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Privacy Policy
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            We are committed to protecting your privacy and ensuring the security of your personal information.
          </p>
          <p className="text-sm text-gray-500 mt-4">
            Last updated: October 2025
          </p>
        </div>
      </section>

      {/* Privacy Policy Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Introduction */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <Eye size={24} className="text-orange-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">1. Information We Collect</h2>
            </div>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 mb-4">
                Masonry Homes LLC ("we," "our," or "us") collects information you provide directly to us when you:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6">
                <li>Fill out our contact form on our website</li>
                <li>Request a quote or consultation</li>
                <li>Communicate with us via email or phone</li>
                <li>Subscribe to our newsletters or updates</li>
                <li>Visit our website (through cookies and similar technologies)</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Personal Information</h3>
              <p className="text-gray-700 mb-4">
                The personal information we may collect includes:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li><strong>Contact Information:</strong> Name, email address, phone number, mailing address</li>
                <li><strong>Project Information:</strong> Project type, budget range, timeline, and specific requirements</li>
                <li><strong>Communication Records:</strong> Messages, inquiries, and correspondence with our team</li>
                <li><strong>Technical Information:</strong> IP address, browser type, device information, and website usage data</li>
              </ul>
            </div>
          </div>

          {/* How We Use Information */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <UserCheck size={24} className="text-orange-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">2. How We Use Your Information</h2>
            </div>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 mb-4">
                We use the information we collect for the following purposes:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6">
                <li>To respond to your inquiries and provide requested services</li>
                <li>To prepare quotes and proposals for construction projects</li>
                <li>To communicate with you about your project and our services</li>
                <li>To schedule consultations and site visits</li>
                <li>To improve our website and services</li>
                <li>To send you updates about our company (with your consent)</li>
                <li>To comply with legal obligations and protect our rights</li>
              </ul>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 mb-6">
                <h4 className="font-semibold text-orange-800 mb-2">Email Communications</h4>
                <p className="text-orange-700 text-sm">
                  When you submit our contact form, we will send the information to our team at masonryhomes@yahoo.com 
                  to ensure prompt response to your inquiry. We will use your email address to respond to your request 
                  and may follow up regarding your project needs.
                </p>
              </div>
            </div>
          </div>

          {/* Information Sharing */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <Lock size={24} className="text-orange-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">3. Information Sharing and Disclosure</h2>
            </div>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 mb-4">
                We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6">
                <li><strong>Service Providers:</strong> With contractors, suppliers, or professionals who assist in your project (with your consent)</li>
                <li><strong>Legal Requirements:</strong> When required by law, regulation, or legal process</li>
                <li><strong>Business Protection:</strong> To protect our rights, property, or safety, or that of our customers</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, sale, or transfer of assets</li>
              </ul>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h4 className="font-semibold text-blue-800 mb-2">Third-Party Services</h4>
                <p className="text-blue-700 text-sm">
                  Our website uses SendGrid for email delivery and MongoDB for data storage. These services 
                  have their own privacy policies and security measures to protect your information.
                </p>
              </div>
            </div>
          </div>

          {/* Data Security */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <Shield size={24} className="text-orange-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">4. Data Security</h2>
            </div>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 mb-4">
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6">
                <li>Secure data transmission using SSL encryption</li>
                <li>Secure database storage with access controls</li>
                <li>Regular security assessments and updates</li>
                <li>Limited access to personal information on a need-to-know basis</li>
                <li>Employee training on data protection practices</li>
              </ul>
              <p className="text-gray-700">
                However, no method of transmission over the internet or electronic storage is 100% secure. 
                While we strive to protect your personal information, we cannot guarantee its absolute security.
              </p>
            </div>
          </div>

          {/* Your Rights */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <FileText size={24} className="text-orange-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">5. Your Rights and Choices</h2>
            </div>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 mb-4">
                You have the following rights regarding your personal information:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6">
                <li><strong>Access:</strong> Request a copy of the personal information we have about you</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal requirements)</li>
                <li><strong>Portability:</strong> Request transfer of your information to another service provider</li>
                <li><strong>Opt-out:</strong> Unsubscribe from marketing communications at any time</li>
              </ul>
              <p className="text-gray-700">
                To exercise these rights, please contact us using the information provided in the "Contact Us" section below.
              </p>
            </div>
          </div>

          {/* Cookies */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <Eye size={24} className="text-orange-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">6. Cookies and Tracking Technologies</h2>
            </div>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 mb-4">
                Our website may use cookies and similar tracking technologies to improve your experience:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6">
                <li><strong>Essential Cookies:</strong> Necessary for the website to function properly</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our website</li>
                <li><strong>Functionality Cookies:</strong> Remember your preferences and settings</li>
              </ul>
              <p className="text-gray-700">
                You can control cookies through your browser settings. However, disabling certain cookies may affect website functionality.
              </p>
            </div>
          </div>

          {/* Children's Privacy */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <Shield size={24} className="text-orange-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">7. Children's Privacy</h2>
            </div>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700">
                Our services are not intended for children under 13 years of age. We do not knowingly collect 
                personal information from children under 13. If we become aware that we have collected personal 
                information from a child under 13, we will take steps to delete such information promptly.
              </p>
            </div>
          </div>

          {/* Changes to Policy */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <FileText size={24} className="text-orange-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">8. Changes to This Privacy Policy</h2>
            </div>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 mb-4">
                We may update this Privacy Policy from time to time. When we do, we will:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
                <li>Post the updated policy on our website</li>
                <li>Update the "Last updated" date at the top of this policy</li>
                <li>Notify you of significant changes via email (if you have provided your email address)</li>
              </ul>
              <p className="text-gray-700">
                Your continued use of our services after any changes indicates your acceptance of the updated Privacy Policy.
              </p>
            </div>
          </div>

          {/* Contact Information */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <Mail size={24} className="text-orange-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">9. Contact Us</h2>
            </div>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 mb-4">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <div className="space-y-2 text-gray-700">
                  <p><strong>Masonry Homes LLC</strong></p>
                  <p>📧 Email: masonryhomes@yahoo.com</p>
                  <p>📞 Phone: (847) 401-5381</p>
                  
                </div>
              </div>
              <p className="text-gray-700 mt-4 text-sm">
                We will respond to your inquiry within 30 days of receipt.
              </p>
            </div>
          </div>

          
        </div>
      </section>
    </div>
  );
};